import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Upload,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Building2,
  Link,
  FileSpreadsheet,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';


const AlloggiatiAssociation = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
    wskey: ''
  });
  const [csvContent, setCsvContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  
  const token = localStorage.getItem('hw_token');

  // Download Excel con immobili senza Questura
  const handleDownloadWithoutQuestura = async () => {
    setDownloadingExcel(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/export-properties-without-questura`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Errore nel download');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `immobili_senza_questura_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Excel scaricato con successo!');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Errore nel download del file');
    }
    setDownloadingExcel(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvContent(event.target.result);
        toast.success(`File ${file.name} caricato`);
      };
      reader.readAsText(file);
    }
  };

  const handleAssociate = async () => {
    if (!credentials.username || !credentials.password || !credentials.wskey) {
      toast.error('Inserisci tutte le credenziali AlloggiatiWeb');
      return;
    }
    if (!csvContent) {
      toast.error('Carica il file CSV degli appartamenti');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      // Parse CSV
      const lines = csvContent.split('\n');
      const header = lines[0].split(';');
      
      const apartments = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';');
        if (values.length >= 2 && values[0].trim()) {
          apartments.push({
            idapp: values[0]?.trim() || '',
            descrizione: values[1]?.trim() || '',
            comune: values[2]?.trim() || '',
            indirizzo: values[4]?.trim() || '',
            proprietario: values[5]?.trim() || ''
          });
        }
      }

      const res = await fetch(`${API_URL}/api/alloggiati/bulk-associate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          credentials: {
            username: credentials.username,
            password: credentials.password,
            wskey: credentials.wskey,
            structure_code: credentials.username
          },
          apartments: apartments
        })
      });

      const data = await res.json();

      if (res.ok) {
        setResults(data);
        toast.success(`Associazione completata: ${data.summary?.matched_count || 0} immobili associati`);
      } else {
        toast.error(data.detail || 'Errore associazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="alloggiati-association-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Associazione AlloggiatiWeb
          </h1>
          <p className="text-slate-500 mt-1">
            Associa automaticamente gli appartamenti del portale Questura agli immobili nel sistema
          </p>
        </div>

        {/* Credenziali */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Link className="h-5 w-5 text-blue-600" />
            Credenziali AlloggiatiWeb
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Username (Codice Struttura)</Label>
              <Input
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                placeholder="Es: GE012149"
                className="bg-white"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                placeholder="Password portale"
                className="bg-white"
              />
            </div>
            <div>
              <Label>WSKey (Chiave Web Service)</Label>
              <Input
                value={credentials.wskey}
                onChange={(e) => setCredentials({ ...credentials, wskey: e.target.value })}
                placeholder="Chiave generata dal portale"
                className="bg-white"
              />
            </div>
          </div>
        </Card>

        {/* CSV Upload */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            File CSV Appartamenti
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Carica file CSV (esportato da AlloggiatiWeb)</Label>
              <p className="text-xs text-slate-500 mb-2">
                Formato: IDAPP;Descrizione;COMUNE;PROV;Indirizzo;Proprietario
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            {csvContent && (
              <div>
                <Label>Anteprima CSV ({csvContent.split('\n').length - 1} righe)</Label>
                <Textarea
                  value={csvContent.substring(0, 1000) + (csvContent.length > 1000 ? '...' : '')}
                  readOnly
                  rows={6}
                  className="font-mono text-xs bg-slate-50"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Action Button */}
        <div className="flex justify-center">
          <Button
            onClick={handleAssociate}
            disabled={loading || !credentials.username || !csvContent}
            className="bg-blue-600 hover:bg-blue-700 px-8 py-3 text-lg"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Upload className="h-5 w-5 mr-2" />
            )}
            Avvia Associazione Automatica
          </Button>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <Card className="p-6 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-800 mb-4">Riepilogo</h2>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-green-100 rounded-lg">
                  <p className="text-3xl font-bold text-green-700">{results.summary?.matched_count || 0}</p>
                  <p className="text-sm text-green-600">Associati</p>
                </div>
                <div className="p-4 bg-yellow-100 rounded-lg">
                  <p className="text-3xl font-bold text-yellow-700">{results.summary?.not_found_count || 0}</p>
                  <p className="text-sm text-yellow-600">Non trovati</p>
                </div>
                <div className="p-4 bg-red-100 rounded-lg">
                  <p className="text-3xl font-bold text-red-700">{results.summary?.errors_count || 0}</p>
                  <p className="text-sm text-red-600">Errori</p>
                </div>
              </div>
            </Card>

            {/* Matched */}
            {results.matched?.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  Immobili Associati ({results.matched.length})
                </h3>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">ID App</th>
                        <th className="px-3 py-2 text-left">Appartamento</th>
                        <th className="px-3 py-2 text-left">Immobile DB</th>
                        <th className="px-3 py-2 text-left">Codice</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {results.matched.map((m, i) => (
                        <tr key={i} className="hover:bg-green-50">
                          <td className="px-3 py-2 font-mono">{m.apartment_id}</td>
                          <td className="px-3 py-2">{m.apartment_name}</td>
                          <td className="px-3 py-2 text-green-700">{m.property_name}</td>
                          <td className="px-3 py-2 text-slate-500">{m.property_code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Not Found */}
            {results.not_found?.length > 0 && (
              <Card className="p-6">
                <h3 className="font-semibold text-yellow-700 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Non Trovati ({results.not_found.length})
                </h3>
                <p className="text-xs text-slate-500 mb-3">
                  Questi appartamenti non hanno trovato corrispondenza nel database. Verificare i nomi o associare manualmente.
                </p>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">ID App</th>
                        <th className="px-3 py-2 text-left">Appartamento</th>
                        <th className="px-3 py-2 text-left">Comune</th>
                        <th className="px-3 py-2 text-left">Indirizzo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {results.not_found.map((n, i) => (
                        <tr key={i} className="hover:bg-yellow-50">
                          <td className="px-3 py-2 font-mono">{n.apartment_id}</td>
                          <td className="px-3 py-2">{n.apartment_name}</td>
                          <td className="px-3 py-2 text-slate-500">{n.comune}</td>
                          <td className="px-3 py-2 text-slate-500 text-xs">{n.indirizzo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Errors */}
            {results.errors?.length > 0 && (
              <Card className="p-6 border-red-200">
                <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <X className="h-5 w-5" />
                  Errori ({results.errors.length})
                </h3>
                <div className="space-y-2">
                  {results.errors.map((e, i) => (
                    <div key={i} className="p-2 bg-red-50 rounded text-sm">
                      <span className="font-medium">{e.apartment_name}</span>: {e.error}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AlloggiatiAssociation;
